import React, { useContext, useEffect, useState } from "react";
import NotesCard from "../page-components/dashboard/note-card/notes-card";
import Quotes from "../page-components/dashboard/quotes/quotes";
import HolidayCard from "../page-components/dashboard/holiday-card/holiday-card";
import Celebrations from "../page-components/dashboard/celebrations/celebrations";
import EventsCard from "../page-components/dashboard/events-card/events-card";
import UserLoginContext from "../context/userLoginContext";
import AdminAttendanceCard from "../page-components/dashboard/admin-attendance-card/admin-attendance-card";
import AssetRequestSummary from "../page-components/dashboard/asset-request-summary-chart/asset-request-summary";
import LeaveRequestSummary from "../page-components/dashboard/leave-request-summary/leaves-request-summary";
import EmployeePunchCard from "../page-components/dashboard/employee-punch-card/employee-punch-card";
import EmployeeLeaveSummary from "../page-components/dashboard/employee-leave-summary/employee-leave-summary";
import EmployeeAttendanceAnalysis
    from "../page-components/dashboard/employee-attendance-anaylsis/employee-attendance-analysis";
import { DragDropProvider, DragEndEvent } from "@dnd-kit/react";
import { isSortable } from "@dnd-kit/react/sortable";
import { move } from "@dnd-kit/helpers";
import DashboardWidget from "../page-components/dashboard/dashboard-widget";
import "../styles/component/dashboard/dashboard.scss";
import "../styles/component/dashboard/dashboard-cards.scss";
import PollVoteCard from "../page-components/dashboard/poll-vote-card/poll-vote-card";
import AdminPollVoteCard from "../page-components/dashboard/admin-poll-vote-card/admin-poll-vote-card";
import ArticlesCard from "../page-components/dashboard/articles-card/articles-card";

interface DashBoardWidgets {
    id: string,
    component: React.FC
}

const ADMIN_WIDGETS = [
    {id: "attendance", component: AdminAttendanceCard},
    {id: "asset", component: AssetRequestSummary},
    {id: "leave", component: LeaveRequestSummary},
    {id: "notes", component: NotesCard},
    {id: "events", component: EventsCard},
    {id: "celebrations", component: Celebrations},
    {id: "holiday", component: HolidayCard},
    {id: "adminPollVote", component: AdminPollVoteCard},
    {id: 'articles', component: ArticlesCard}

];

const EMPLOYEE_WIDGETS = [
    {id: "punch", component: EmployeePunchCard},
    {id: "attendance", component: EmployeeAttendanceAnalysis},
    {id: "leave", component: EmployeeLeaveSummary},
    {id: "notes", component: NotesCard},
    {id: "events", component: EventsCard},
    {id: "celebrations", component: Celebrations},
    {id: "holiday", component: HolidayCard},
    {id: 'pollVote', component: PollVoteCard},
    {id: 'articles', component: ArticlesCard}
];

const USER_WIDGETS = [
    {id: "notes", component: NotesCard},
    {id: "events", component: EventsCard},
    {id: "celebrations", component: Celebrations},
    {id: "holiday", component: HolidayCard},
    {id: 'articles', component: ArticlesCard}
]


function Dashboard() {
    const {user} = useContext(UserLoginContext);
    const [widgets, setWidgets] = useState<DashBoardWidgets[]>([]);

    const storageKey = React.useMemo(() => {
        if (user.isAdmin) {
            return `dashboard-layout-admin-${user.id}`;
        }

        if (user.employeeId) {
            return `dashboard-layout-employee-${user.employeeId}`;
        }

        return `dashboard-layout-user-${user.id}`;
    }, [user]);

    useEffect(() => {
        const defaultWidgets = user.isAdmin
            ? ADMIN_WIDGETS
            : user.employeeId
                ? EMPLOYEE_WIDGETS
                : USER_WIDGETS;

        const savedOrder = localStorage.getItem(storageKey);

        if (!savedOrder) {
            setWidgets(defaultWidgets);
            return;
        }

        try {
            const order: string[] = JSON.parse(savedOrder);

            const widgetMap = new Map(
                defaultWidgets.map(widget => [widget.id, widget])
            );

            const sorted = order
                .map(id => widgetMap.get(id))
                .filter(Boolean) as DashBoardWidgets[];

            // Add any new widgets introduced after a deployment
            const remaining = defaultWidgets.filter(
                widget => !order.includes(widget.id)
            );

            setWidgets([...sorted, ...remaining]);
        } catch {
            setWidgets(defaultWidgets);
        }
    }, [user, storageKey]);

    useEffect(() => {
        if (!widgets.length) {
            return;
        }

        localStorage.setItem(
            storageKey,
            JSON.stringify(widgets.map(widget => widget.id))
        );
    }, [widgets, storageKey]);

    function handleDragEnd(event: DragEndEvent) {
        const {operation} = event;

        if (!operation.target || !isSortable(operation.source)) {
            return;
        }

        setWidgets(current => move(current, event));
    };


    return (
        <div className="hrms-dashboard">
            <div className="hrms-dashboard__container">

                <Quotes/>

                <DragDropProvider
                    onDragEnd={handleDragEnd}
                >
                    <div className="dashboard-grid">
                        {widgets && widgets.map(({id, component: Component}, index) => (
                            <DashboardWidget
                                key={id}
                                id={id}
                                index={index}
                            >
                                <Component/>
                            </DashboardWidget>
                        ))}
                    </div>
                </DragDropProvider>

            </div>
        </div>
    );
}

export default Dashboard;
